import { ChangeDetectorRef, Component, HostListener, ViewChild, inject } from "@angular/core";
import { CropperSettings, ImageCropperComponent } from "ngx-img-cropper";
import { VNCImageCropperComponent, VNCHeaderComponent, VNCLoaderCircularComponent, VNCMobileGalleryComponent } from "@uxf/vnc-library";
import { AuthUser } from "../../models";
import { TaskRepository } from "../../repository/task.repository";
import { Broadcaster } from "src/app/common/providers/broadcaster.service";
import { MatDialogRef } from "@angular/material/dialog";
import { getAuthUser, SetIsLoading, TasksRootState } from "../../store";
import { Store } from "@ngrx/store";
import { TaskService } from "../../task.service";
import { takeWhile } from "rxjs";
import { getOnlineStatus } from "src/app/reducers";
import { NgStyle, UpperCasePipe } from "@angular/common";
import { MatSlider, MatSliderThumb } from "@angular/material/slider";
import { FormsModule } from "@angular/forms";
import { MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
    selector: "vp-task-image-desktop-cropper",
    templateUrl: "./task-image-desktop-cropper.html",
    imports: [VNCHeaderComponent, VNCLoaderCircularComponent, NgStyle, VNCImageCropperComponent, VNCMobileGalleryComponent, MatSlider, FormsModule, MatSliderThumb, MatButton, MatIcon, UpperCasePipe, TranslatePipe]
})
export class TaskImageDesktopCropperComponent {
    private changeDetection = inject(ChangeDetectorRef);
    private tasksRepo = inject(TaskRepository);
    private broadcaster = inject(Broadcaster);
    private dialogRef = inject<MatDialogRef<TaskImageDesktopCropperComponent>>(MatDialogRef);
    private store = inject<Store<TasksRootState>>(Store);
    private taskService = inject(TaskService);

    @ViewChild(VNCImageCropperComponent) vncImageCropperComponent: VNCImageCropperComponent;
    defaultCovers: Array<{ name: string; url: string }> = [];
    externalCover: Array<{ name: string; url: string; filename?: string; base64data?: string; type?: string; id?: string }> = [];
    visible = false;
    indexItem: number | null = null;
    externalIndex: number | null = null;
    zoom = 1;
    minZoom = 0.2;
    maxZoom = 3;
    addTaskInProgress = false;
    pendingSave = false;
    croppedDataUrl: string | null = null;
    isDesktop = false;
    private sourceKind: "upload" | "default" | "external" | "existing" = "existing";
    showDefaultAvatar: boolean = false;
    avatarSelected: boolean = false;
    hideCropper: boolean = false;
    isDraggable: boolean = true;
    min: number = 0.5;
    max: number = 1;
    cropperEvent: any;
    croppedImage: any = "";
    canvasRotation = 0;
    rotation = 0;
    scale = 1;
    imageData: any;
    cropperSettings: CropperSettings;
    @ViewChild("cropper", { static: false })
    cropper: ImageCropperComponent;
    isFileUploded = false;
    msgs: any;
    isAlive = true;
    user: AuthUser;
    isOnline = false;
    avatarData: any;

    constructor() {
        this.broadcaster.on<any>("closeAllMdlDialogs").pipe(takeWhile(() => this.isAlive))
            .subscribe(presence => {
                this.hide();
            });
        this.broadcaster.on<any>("hideTaskImageCropperDialog").pipe(takeWhile(() => this.isAlive))
            .subscribe(presence => {
                this.hide();
            });

        this.store.select(getOnlineStatus).pipe(takeWhile(() => this.isAlive)).subscribe((isOnline) => {
            this.isOnline = isOnline;
            this.changeDetection.markForCheck();
        });

        this.store.select(getAuthUser).pipe(takeWhile(() => this.isAlive)).subscribe(user => {
            if (user) {
                this.user = user;
                if (this.isOnline) {
                    this.avatarData = this.user.userAvatar;
                    setTimeout(() => {
                        this.resetCropper();
                        this.loadExistingCropperAvatar(this.tasksRepo.buildAvatarUrl(this.user.jid, new Date().getTime()));
                    }, 200);
                }
            }
        });
        setTimeout(() => {
            this.visible = true;
        }, 1000);
    }
    ngOnInit(): void {
        this.isDesktop = window.innerWidth >= 768;
        for (let i = 1; i < 15; i++) {
            const text = i.toString().padStart(3, "0");
            this.defaultCovers.push({
                name: `default-pattern-sq-${text}.png`,
                url: `assets/avatar/default-pattern-sq-${text}.png`,
            });
        }
    }
    ngOnDestroy(): void {
        this.isAlive = false;
        this.changeDetection.markForCheck();
    }

    @HostListener("document:keydown.esc")
    hide(): void {
        this.isFileUploded = false;
        this.croppedDataUrl = null;
        this.pendingSave = false;
        this.dialogRef.close();
        this.changeDetection.markForCheck();
    }

    resetCropper() {
        this.isFileUploded = false;
        this.croppedDataUrl = null;
        this.pendingSave = false;
        this.changeDetection.markForCheck();
    }

    saveCropperImge(dataUrl?: string) {
        if (!this.isOnline) {
            this.broadcaster.broadcast("OFFLINE_CONNECTION", true);
            return;
        }
        if (dataUrl) {
            try {
                this.broadcaster.broadcast("profileAvatarUpdatedLocal", {
                    id: this.user?.id,
                    jid: this.user?.jid,
                    avatar: dataUrl
                });
                this.tasksRepo.updateUserAvatar(dataUrl, this.user.id, this.user.jid);
            } finally {
                this.store.dispatch(new SetIsLoading(false));
                this.hide();
            }
        } else {
            this.pendingSave = true;
            this.store.dispatch(new SetIsLoading(true));
            this.vncImageCropperComponent?.cropImage();
        }
    }
    removeAvatar() {
        if (this.isOnline) {
            this.store.dispatch(new SetIsLoading(true));
            this.tasksRepo.removeUserAvatar();
            this.hide();
        } else {
            this.broadcaster.broadcast("OFFLINE_CONNECTION", true);
        }
    }

    fileChangeListener($event: Event) {
        const input = $event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) {
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            this.sourceKind = "upload";
            this.isDraggable = true;
            this.setOriginalFromSrc(reader.result as string);
        };
        reader.readAsDataURL(file);
    }
    selectIndexItem(i: number, isExternal = false) {
        this.indexItem = isExternal ? this.indexItem : i;
        this.externalIndex = isExternal ? i : this.externalIndex;
        const src = isExternal
            ? (this.externalCover[i]?.base64data || this.externalCover[i]?.url)
            : this.defaultCovers[i]?.url;
        if (!src) {
          return;
        }
        this.sourceKind = isExternal ? "external" : "default";
        this.isDraggable = false;
        this.setOriginalFromSrc(src);

        this.isFileUploded = true;
        this.changeDetection.markForCheck();
    }

    loadExistingCropperAvatar(url: string) {
        this.sourceKind = "existing";
        this.isDraggable = false;
        this.setOriginalFromSrc(url);
    }
    loadedImageForCrop($event: any) {
        this.visible = true;
        this.avatarSelected = true;
        this.hideCropper = false;
        this.isFileUploded = true;
        this.scale = 1;
        this.changeDetection.markForCheck();
    }

    private setOriginalFromSrc(src: string) {
        if (!this.vncImageCropperComponent) {
          return;
        }
        this.vncImageCropperComponent.cropper.loadImage({ originalDataURL: src });
        this.isFileUploded = true;
        this.visible = false;
        this.changeDetection.markForCheck();
    }
    imageCropped(event: { dataURL: string; blob?: Blob }) {
        this.cropperEvent = event;
        this.croppedImage = event.dataURL;
        this.isFileUploded = true;

        if (this.pendingSave) {
            this.pendingSave = false;
            this.saveCropperImge(event.dataURL);
        }
        this.changeDetection.markForCheck();
    }
}